import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { checkEmailInAllCollections } from '../utils/emailValidator';
import { useNavigate, useSearchParams } from "react-router-dom";

import { 
  createUniqueReferralCode, 
  validateReferralCode 
} from "../utils/referralSystem";

function UserDetails() {
  const [user, setUser] = useState(null);
  const [searchParams] = useSearchParams();
  const [userEmail, setUserEmail] = useState("");
  
  const [formData, setFormData] = useState({
    isAdult: false,
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    address: "",
    accountType: "Direct Fund Management",
    investmentExperience: "",
    bankAccountDetails: "",
    referralCode: searchParams.get('ref') || "",
    investmentAmount: "",
    currency: "$",
    riskTolerance: "",
    investmentGoals: "",
    preferredCommunication: "email",
  });

  const [errors, setErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [permissionError, setPermissionError] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralInfo, setReferralInfo] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [referralProcessResult, _setReferralProcessResult] = useState(null);
  const navigate = useNavigate();

  // Handle referral code from URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    console.log("🔍 Checking URL for referral code:", refCode);
    
    if (refCode) {
      setFormData(prev => ({
        ...prev,
        referralCode: refCode
      }));
      console.log("✅ Set referral code from URL:", refCode);
      validateReferralCodeAsync(refCode);
    }
  }, [searchParams]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
        const userEmail = user.email;
        setUserEmail(userEmail);
        setFormData((prevData) => ({
          ...prevData,
          emailAddress: userEmail,
        }));

        const checkExistingAccounts = async () => {
          try {
            const existingAccounts = await checkEmailInAllCollections(userEmail);
            
            if (existingAccounts.length > 0) {
              const accountTypes = existingAccounts.map(acc => {
                switch(acc.type) {
                  case 'investment': return 'VonScalper Copy Trading';
                  case 'training': return 'Training Program';
                  case 'general': return 'Investment Account';
                  default: return acc.type;
                }
              }).join(', ');
              
              setPermissionError(`You already have accounts for: ${accountTypes}. Please use the dashboard selection to access your services.`);
              
              setTimeout(() => {
                const goToDashboard = window.confirm(
                  `You have existing accounts for: ${accountTypes}\n\nWould you like to go to your dashboard selection?`
                );
                
                if (goToDashboard) {
                  navigate('/dashboard-selection');
                }
              }, 2000);
              return;
            }
            
            // Also check specific Investors collection for this component
            const userDocRef = doc(db, "Investors", user.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists()) {
              setFormData(userDocSnap.data());
              navigate('/userdash'); 
            }
          } catch (error) {
            console.error("Error checking existing accounts:", error);
            if (error.code === 'permission-denied') {
              setPermissionError("Access denied. Please contact support.");
            } else {
              setPermissionError("Error loading your data. Please try again.");
            }
          }
        };
        
        checkExistingAccounts();
      } else {
        navigate("/auth");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'emailAddress') {
      return;
    }
    
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    if (permissionError) {
      setPermissionError("");
    }

    if (name === "referralCode" && value) {
      validateReferralCodeAsync(value);
    } else if (name === "referralCode" && !value) {
      setReferralValid(null);
      setReferralInfo(null);
    }
  };

  const validateReferralCodeAsync = async (code) => {
    if (!code || code.trim().length < 6) {
      setReferralValid(null);
      setReferralInfo(null);
      return;
    }

    console.log("🔍 Validating referral code:", code);

    try {
      const validation = await validateReferralCode(code.trim().toUpperCase());
      console.log("✅ Referral validation result:", validation);
      
      if (validation.valid) {
        setReferralValid(true);
        setReferralInfo(validation);
        console.log("✅ Valid referral code - Referrer:", validation.referrerName);
      } else {
        setReferralValid(false);
        setReferralInfo(null);
        console.log("❌ Invalid referral code:", validation.error);
      }
    } catch (error) {
      console.error("❌ Error validating referral code:", error);
      setReferralValid(false);
      setReferralInfo(null);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone Number is required";
      isValid = false;
    } else if (!/^\d{11}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Invalid Phone Number (11 digits required)";
      isValid = false;
    }

    if (!formData.isAdult) {
      newErrors.isAdult = "You must be at least 18 years old to proceed.";
      isValid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!formData.investmentAmount.trim()) {
      newErrors.investmentAmount = "Investment Amount is required";
      isValid = false;
    } else if (
      !/^[0-9,]+(\.[0-9]{1,8})?$/.test(formData.investmentAmount) 
    ) {
      newErrors.investmentAmount = "Invalid Investment Amount format";
      isValid = false;
    }

    if (!formData.bankAccountDetails.trim()) {
      newErrors.bankAccountDetails = "Bank Account Details are required";
      isValid = false;
    }

    if (!formData.investmentExperience) {
      newErrors.investmentExperience = "Please select your investment experience level";
      isValid = false;
    }

    if (!formData.riskTolerance) {
      newErrors.riskTolerance = "Please select your risk tolerance";
      isValid = false;
    }

    if (!formData.investmentGoals) {
      newErrors.investmentGoals = "Please select your primary investment goal";
      isValid = false;
    }

    if (formData.referralCode && referralValid === false) {
      newErrors.referralCode = "Invalid referral code";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionStatus("submitting");
    setPermissionError("");

    if (!validateForm()) {
      setSubmissionStatus("error");
      return;
    }

    try {
      // Check for existing accounts before creating new one
      const existingAccounts = await checkEmailInAllCollections(formData.emailAddress);
      
      if (existingAccounts.length > 0) {
        const accountTypes = existingAccounts.map(acc => {
          switch(acc.type) {
            case 'investment': return 'VonScalper Copy Trading';
            case 'training': return 'Training Program';
            case 'general': return 'Investment Account';
            default: return acc.type;
          }
        }).join(', ');
        
        setSubmissionStatus("error");
        setPermissionError(`You already have accounts for: ${accountTypes}. Please use the dashboard selection to access your services.`);
        
        setTimeout(() => {
          navigate('/dashboard-selection');
        }, 3000);
        
        return;
      }

      const userDocRef = doc(db, "Investors", user.uid);
      const sanitizedAmount = formData.investmentAmount.replace(/[^0-9.]/g, "");
      const investmentAmount = parseFloat(sanitizedAmount);

      const newUserReferralCode = await createUniqueReferralCode(formData.fullName, user.uid);

      const finalUserData = {
        ...formData,
        emailAddress: user.email,
        investmentAmount: investmentAmount,
        userId: user.uid,
        firebaseUid: user.uid,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        isActive: true,
        profileCompleted: true,
        kycStatus: "pending",
        accountStatus: "active",
        
        referralCode: newUserReferralCode,
        referralStats: {
          totalReferrals: 0,
          successfulReferrals: 0,
          totalBonusEarned: 0,
          createdAt: serverTimestamp()
        },
        
        hasReferralCode: false,
        referralPending: !!formData.referralCode,
        pendingReferralCode: formData.referralCode || null,
        
        riskProfile: {
          tolerance: formData.riskTolerance,
          experience: formData.investmentExperience,
          goals: formData.investmentGoals
        },
        notifications: {
          email: true,
          sms: formData.preferredCommunication.includes('sms'),
          whatsapp: formData.preferredCommunication.includes('whatsapp')
        }
      };

      console.log("Saving user document...");
      await setDoc(userDocRef, finalUserData);
      console.log("User document saved successfully!");

      setSubmissionStatus("success");
      
      if (formData.referralCode) {
        alert("Your account has been created! You can add your referral code in your dashboard after logging in.");
      }
      
      setTimeout(() => {
        navigate("/ConfettiCelebration");
      }, 1500);
      
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setSubmissionStatus("error");
      setPermissionError(`An error occurred: ${error.message}`);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.3)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderRadius: "0.5rem",
        padding: "2rem",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        width: "100%",
        maxWidth: "80%",
        margin: "0 auto",
        maxHeight: "80vh",
        overflowY: "auto",
        color: "whitesmoke",
        zIndex: 1,
        marginTop: "10vh",
      }}
    >
      {permissionError && !permissionError.includes('already have accounts') && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg">
          <strong>Error:</strong> {permissionError}
          <div className="mt-2">
            <button 
              onClick={() => window.location.reload()} 
              className="text-sm underline hover:no-underline"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {permissionError && permissionError.includes('already have accounts') && (
        <div className="mb-4 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
          <h4 className="text-blue-300 font-semibold mb-2">ℹ️ Account Already Exists!</h4>
          <p className="text-sm mb-4">{permissionError}</p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => navigate('/dashboard-selection')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-all font-semibold"
            >
              📊 Go to My Accounts
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-all font-semibold"
            >
              🔐 Sign In Instead
            </button>
          </div>
        </div>
      )}

      {submissionStatus === "submitting" && (
        <div className="flex items-center justify-center mb-4 p-4 bg-blue-500/20 border border-blue-500/50 text-blue-300 rounded-lg">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400 mr-3"></div>
          <div>Creating your investment account...</div>
        </div>
      )}

      {submissionStatus === "success" && (
        <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 text-green-300 rounded-lg">
          <strong>Success!</strong> Account created successfully! 
          {referralProcessResult && (
            <div className="mt-2 text-sm">
              🎉 Referral processed! You were referred by {referralProcessResult.referrerName}
            </div>
          )}
          Redirecting...
        </div>
      )}

      {submissionStatus === "error" && !permissionError && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 text-red-300 rounded-lg">
          <strong>Error:</strong> Failed to create account. Please try again.
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-4">
        <h2 className="text-2xl font-bold mb-4 text-center">Create Your Enhanced Investment Account</h2>
        <p className="text-center text-gray-300 mb-6">
          🚀 Welcome {userEmail ? userEmail.split('@')[0] : 'Investor'}! Get access to personalized rates, referral bonuses, and exclusive promotions
        </p>
        
        <div className="mb-6 p-4 bg-white/10 rounded-lg">
          <a href="/agreement" target="_blank" rel="noopener noreferrer">
            <button
              type="button"
              className="w-full bg-gray-500 text-white py-2 rounded-md hover:bg-gray-600 transition duration-200 mb-4"
            >
              📋 Read Terms & Conditions
            </button>
          </a>
          
          <div className="flex items-start">
            <input 
              type="checkbox" 
              id="isAdult" 
              name="isAdult" 
              checked={formData.isAdult} 
              onChange={handleChange}
              className="mt-1 mr-3"
            />
            <label htmlFor="isAdult" className="text-sm">
              I confirm that I am at least 18 years old and have read and agree to the terms and conditions.
            </label>
          </div>
          {errors.isAdult && (
            <div className="text-red-400 text-sm mt-2">{errors.isAdult}</div> 
          )}
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-blue-300">Personal Information</h3>
          
          <div className="mb-4">
            <label htmlFor="fullName" className="block text-sm font-medium mb-2">
              Full Name *
            </label>
            <input
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full legal name"
            />
            {errors.fullName && (
              <div className="text-red-400 text-sm mt-1">{errors.fullName}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="emailAddress" className="block text-sm font-medium mb-2">
              Email Address *
            </label>
            <input
              className="w-full border border-gray-400 rounded-md shadow-sm p-3 text-gray-600 bg-gray-100 cursor-not-allowed"
              type="email"
              id="emailAddress"
              name="emailAddress"
              value={formData.emailAddress}
              readOnly
            />
            <div className="flex items-center gap-2 text-blue-400 text-sm mt-1">
              <span>🔒</span> 
              <span>This email is linked to your account and cannot be changed</span>
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="phoneNumber" className="block text-sm font-medium mb-2">
              Phone Number *
            </label>
            <input
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="e.g., 08012345678"
            />
            {errors.phoneNumber && (
              <div className="text-red-400 text-sm mt-1">{errors.phoneNumber}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="address" className="block text-sm font-medium mb-2">
              Address *
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your full address"
              rows="2"
            />
            {errors.address && (
              <div className="text-red-400 text-sm mt-1">{errors.address}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="referralCode" className="block text-sm font-medium mb-2">
              Referral Code (Optional) 🎁
            </label>
            <div className="relative">
              <input
                className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black pr-10"
                type="text"
                id="referralCode"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
                placeholder="Enter referral code if you have one"
                style={{ textTransform: 'uppercase' }}
              />
              {referralValid === true && (
                <div className="absolute right-3 top-3 text-green-500">✓</div>
              )}
              {referralValid === false && (
                <div className="absolute right-3 top-3 text-red-500">✗</div>
              )}
            </div>
            {errors.referralCode && (
              <div className="text-red-400 text-sm mt-1">{errors.referralCode}</div>
            )}
            {referralValid === true && referralInfo && (
              <div className="text-green-400 text-sm mt-1">
                ✓ Valid referral code! You'll be referred by {referralInfo.referrerName} and get bonus rates when approved.
              </div>
            )}
            {referralValid === false && formData.referralCode && (
              <div className="text-red-400 text-sm mt-1">
                ✗ Invalid referral code. Please check and try again.
              </div>
            )}
            <p className="text-xs text-gray-300 mt-1">
              Get bonus rates and exclusive benefits when referred by existing investors!
            </p>
          </div>

          <div className="mb-4">
            <label htmlFor="preferredCommunication" className="block text-sm font-medium mb-2">
              Preferred Communication Method
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="preferredCommunication"
              name="preferredCommunication"
              value={formData.preferredCommunication}
              onChange={handleChange}
            >
              <option value="email">Email Only</option>
              <option value="email_sms">Email + SMS</option>
              <option value="email_whatsapp">Email + WhatsApp</option>
              <option value="all">All Methods (Email, SMS, WhatsApp)</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-blue-300">Investment Profile</h3>
          
          <div className="mb-4">
            <label htmlFor="accountType" className="block text-sm font-medium mb-2">
              Preferred Account Type
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="accountType"
              name="accountType"
              value={formData.accountType}
              onChange={handleChange}
            >
              <option value="Direct Fund Management">Direct Fund Management</option>
              <option value="Pooled Fund">Pooled Fund</option>
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="investmentExperience" className="block text-sm font-medium mb-2">
              Investment Experience *
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="investmentExperience"
              name="investmentExperience"
              value={formData.investmentExperience}
              onChange={handleChange}
            >
              <option value="">Select Experience Level</option>
              <option value="Beginner">Beginner (New to investing)</option>
              <option value="Intermediate">Intermediate (Some experience)</option>
              <option value="Advanced">Advanced (Experienced investor)</option>
              <option value="Professional">Professional (Finance background)</option>
            </select>
            {errors.investmentExperience && (
              <div className="text-red-400 text-sm mt-1">{errors.investmentExperience}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="riskTolerance" className="block text-sm font-medium mb-2">
              Risk Tolerance *
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="riskTolerance"
              name="riskTolerance"
              value={formData.riskTolerance}
              onChange={handleChange}
            >
              <option value="">Select Risk Level</option>
              <option value="Conservative">Conservative (Lower risk, steady returns)</option>
              <option value="Moderate">Moderate (Balanced risk and return)</option>
              <option value="Aggressive">Aggressive (Higher risk, higher potential returns)</option>
              <option value="Very Aggressive">Very Aggressive (Maximum risk for maximum returns)</option>
            </select>
            {errors.riskTolerance && (
              <div className="text-red-400 text-sm mt-1">{errors.riskTolerance}</div>
            )}
            <p className="text-xs text-gray-300 mt-1">
              Your risk profile helps us offer you the most suitable investment rates.
            </p>
          </div>

          <div className="mb-4">
            <label htmlFor="investmentGoals" className="block text-sm font-medium mb-2">
              Primary Investment Goal *
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="investmentGoals"
              name="investmentGoals"
              value={formData.investmentGoals}
              onChange={handleChange}
            >
              <option value="">Select Primary Goal</option>
              <option value="Wealth Building">Long-term Wealth Building</option>
              <option value="Retirement">Retirement Planning</option>
              <option value="Emergency Fund">Emergency Fund Growth</option>
              <option value="Short Term">Short-term Gains</option>
              <option value="Passive Income">Passive Income Generation</option>
              <option value="Education Fund">Education Fund</option>
              <option value="Property Investment">Property Investment</option>
            </select>
            {errors.investmentGoals && (
              <div className="text-red-400 text-sm mt-1">{errors.investmentGoals}</div>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="currency" className="block text-sm font-medium mb-2">
              Preferred Currency
            </label>
            <select
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="currency"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
            >
              <option value="₦">₦ (Nigerian Naira)</option>
              <option value="$">$ (US Dollar)</option>
              <option value="€">€ (Euro)</option>
              <option value="£">£ (British Pound)</option>
              <option value="BTC">BTC (Bitcoin)</option>
              <option value="ETH">ETH (Ethereum)</option>
              <option value="USDT">USDT (Tether)</option>
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="investmentAmount" className="block text-sm font-medium mb-2">
              Initial Investment Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">{formData.currency}</span>
              <input 
                className="w-full border border-gray-300 rounded-md shadow-sm p-3 pl-12 focus:ring-2 focus:ring-blue-500 text-black"
                type="text"
                id="investmentAmount"
                name="investmentAmount"
                placeholder="Enter amount (e.g., 100000)" 
                value={formData.investmentAmount}
                onChange={(e) => {
                  const input = e.target.value;
                  const sanitizedInput = input.replace(/[^0-9,.]/g, '');
                  setFormData({
                    ...formData,
                    investmentAmount: sanitizedInput,
                  });
                }}
              />
            </div>
            {errors.investmentAmount && (
              <div className="text-red-400 text-sm mt-1">{errors.investmentAmount}</div>
            )}
            <p className="text-xs text-gray-300 mt-1">
              Higher investment amounts may qualify for premium rates and exclusive benefits.
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3 text-blue-300">Banking Information</h3>
          
          <div className="mb-4">
            <label htmlFor="bankAccountDetails" className="block text-sm font-medium mb-2">
              Bank Account Details *
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md shadow-sm p-3 focus:ring-2 focus:ring-blue-500 text-black"
              id="bankAccountDetails"
              name="bankAccountDetails"
              value={formData.bankAccountDetails}
              onChange={handleChange}
              placeholder="Bank Name, Account Number, Account Name, Sort Code (if applicable)"
              rows="3"
            />
            {errors.bankAccountDetails && (
              <div className="text-red-400 text-sm mt-1">{errors.bankAccountDetails}</div>
            )}
            <p className="text-xs text-gray-300 mt-1">
              This information is used for secure deposits and withdrawals.
            </p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-lg border border-blue-500/30">
          <h4 className="text-blue-300 font-semibold mb-2">🚀 Enhanced Investment Features</h4>
          <ul className="text-sm text-gray-300 space-y-1">
            <li>✅ Personalized investment rates based on your profile</li>
            <li>✅ Referral bonuses for successful recommendations</li>
            <li>✅ Exclusive promotional rates and campaigns</li>
            <li>✅ Real-time portfolio tracking with enhanced calculations</li>
            <li>✅ Flexible withdrawal options with smart eligibility</li>
            <li>✅ Risk-appropriate investment recommendations</li>
          </ul>
        </div>

        <button
          type="submit"
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 rounded-md hover:from-blue-600 hover:to-purple-700 transition duration-200 flex items-center justify-center font-medium text-lg"
          disabled={submissionStatus === "submitting"}
        >
          {submissionStatus === "submitting" ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              Creating Account...
            </>
          ) : (
            <>
              🚀 Create Enhanced Account
            </>
          )}
        </button>

        <div className="mt-4 p-3 bg-green-500/20 border border-green-500/30 rounded-lg">
          <p className="text-xs text-green-300 text-center">
            🎯 <strong>What happens next:</strong> Our admin team will verify your information, activate your account, 
            and assign personalized rates based on your profile. You'll receive email confirmation within 24 hours.
          </p>
        </div>
      </form>
    </div>
  );
}

export default UserDetails;